import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ChartBar, TrendUp, Trophy, Target, ArrowLeft, UploadSimple, Trash, Info, Sun, Moon, Scales, Check, ShareNetwork } from "phosphor-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImportAnalyticsModal from "../components/ImportAnalyticsModal";
import ImportPaywallModal from "../components/ImportPaywallModal";
import CryptoPaymentModal from "../components/CryptoPaymentModal";
import ImportedShareModal from "../components/ImportedShareModal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Profit Factor color based on value
const getProfitFactorColor = (pf) => {
  if (pf === null || pf === undefined || pf === "N/A") return "text-white/50";
  const pfValue = parseFloat(pf) || 0;
  if (pfValue < 1.0) return "text-[#FF3B30]";
  if (pfValue <= 1.5) return "text-[#FFC107]";
  return "text-[#00E676]";
};

// Profit Factor friendly label
const getProfitFactorLabel = (pf) => {
  if (pf === null || pf === undefined || pf === "N/A") return "N/A";
  const pfValue = parseFloat(pf);
  if (isNaN(pfValue)) return "N/A";
  if (pfValue === Infinity || pfValue > 1000) return "No losses";
  if (pfValue < 1.0) return "Unprofitable";
  if (pfValue <= 1.5) return "Decent";
  return "Strong";
};

// Format profit factor display value
const formatProfitFactor = (pf) => {
  if (pf === null || pf === undefined) return "N/A";
  if (pf === Infinity || pf > 1000) return "∞";
  return parseFloat(pf).toFixed(2);
};

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
    <div className="flex items-start justify-between mb-3">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={20} weight="duotone" className="text-white/40" />}
    </div>
    <div className={`text-3xl font-bold font-mono mb-1 ${highlight ? highlight : 'text-white'}`}>
      {value}
    </div>
    {subtitle && <div className="text-white/60 text-sm">{subtitle}</div>}
  </div>
);

export default function ImportedAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [importedData, setImportedData] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteBatchConfirm, setShowDeleteBatchConfirm] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);
  
  // Access control
  const [proStatus, setProStatus] = useState({ isPro: false, plan: null });
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isUpgradeFlow, setIsUpgradeFlow] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  
  // Batch filtering
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('all');
  
  // Comparison mode
  const [compareMode, setCompareMode] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonMetric, setComparisonMetric] = useState('win_rate');
  
  // Comparison chart colors
  const COMPARISON_COLORS = ['#00E676', '#00B8D4', '#FFC107', '#FF6B6B'];
  
  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    checkAccessAndFetch();
  }, []);
  
  // Refetch when batch selection changes
  useEffect(() => {
    if (accessChecked && proStatus.plan === 'yearly') {
      fetchImportedData(selectedBatch);
    }
  }, [selectedBatch]);

  // Handle upgrade click from paywall modal - show payment modal with upgrade context
  const handlePaywallUpgradeClick = () => {
    setShowPaywallModal(false);
    setIsUpgradeFlow(true); // Enable upgrade flow for discounted price
    setShowPaymentModal(true);
  };

  const checkAccessAndFetch = async () => {
    try {
      // Check pro status first
      const profileRes = await api.get('/user/profile');
      const profile = profileRes.data;
      
      let hasYearlyAccess = false;
      if (profile.subscription_status === 'pro' && profile.subscription_expires_at) {
        const expiresAt = new Date(profile.subscription_expires_at);
        const isActive = expiresAt > new Date();
        hasYearlyAccess = isActive && profile.subscription_plan === 'yearly';
        setProStatus({
          isPro: isActive,
          plan: profile.subscription_plan
        });
      }
      
      setAccessChecked(true);
      
      // If no yearly access, show paywall
      if (!hasYearlyAccess) {
        setShowPaywallModal(true);
        setLoading(false);
        return;
      }
      
      // Fetch batches and imported data if has access
      fetchBatches();
      fetchImportedData('all');
    } catch (error) {
      console.error("Error checking access:", error);
      setAccessChecked(true);
      setShowPaywallModal(true);
      setLoading(false);
    }
  };
  
  const fetchBatches = async () => {
    try {
      const response = await api.get('/analytics/import-batches');
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchImportedData = async (batchId = 'all') => {
    try {
      setLoading(true);
      const params = batchId && batchId !== 'all' ? `?batch_id=${batchId}` : '';
      const response = await api.get(`/analytics/imported${params}`);
      setImportedData(response.data);
    } catch (error) {
      console.error("Error fetching imported analytics:", error);
      toast.error("Failed to load imported analytics");
    } finally {
      setLoading(false);
    }
  };
  
  // Comparison mode handlers
  const toggleBatchSelection = (batchId) => {
    setSelectedBatchIds(prev => {
      if (prev.includes(batchId)) {
        return prev.filter(id => id !== batchId);
      } else {
        if (prev.length >= 4) {
          toast.error("You can compare up to 4 batches at once");
          return prev;
        }
        return [...prev, batchId];
      }
    });
  };
  
  const fetchComparisonData = async () => {
    if (selectedBatchIds.length < 2) {
      setComparisonData([]);
      return;
    }
    
    setComparisonLoading(true);
    try {
      const response = await api.get(`/analytics/imported/compare?batch_ids=${selectedBatchIds.join(',')}`);
      setComparisonData(response.data.batches || []);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      toast.error("Failed to load comparison data");
    } finally {
      setComparisonLoading(false);
    }
  };
  
  // Fetch comparison data when selected batches change
  useEffect(() => {
    if (compareMode && selectedBatchIds.length >= 2) {
      fetchComparisonData();
    }
  }, [selectedBatchIds, compareMode]);
  
  // Reset comparison when exiting compare mode
  useEffect(() => {
    if (!compareMode) {
      setSelectedBatchIds([]);
      setComparisonData([]);
    }
  }, [compareMode]);
  
  // Helper for profit factor color
  const getProfitFactorColor = (pf) => {
    if (pf >= 2) return 'text-[#00E676]';
    if (pf >= 1) return 'text-[#FFC107]';
    return 'text-[#FF3B30]';
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      await api.delete('/analytics/imported');
      toast.success("All imported data deleted");
      setImportedData({ has_imported_data: false, total_imported: 0, stats: null });
      setBatches([]);
      setSelectedBatch('all');
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting imported data:", error);
      toast.error("Failed to delete imported data");
    } finally {
      setDeleting(false);
    }
  };
  
  const handleDeleteBatch = async () => {
    if (selectedBatch === 'all') return;
    
    setDeletingBatch(true);
    try {
      await api.delete(`/analytics/imported?batch_id=${selectedBatch}`);
      toast.success("Import batch deleted successfully");
      
      // Reset state
      setSelectedBatch('all');
      setShowDeleteBatchConfirm(false);
      
      // Exit compare mode if active
      if (compareMode) {
        setCompareMode(false);
        setSelectedBatchIds([]);
        setComparisonData([]);
      }
      
      // Refresh data
      fetchBatches();
      fetchImportedData('all');
    } catch (error) {
      console.error("Error deleting batch:", error);
      toast.error("Failed to delete batch");
    } finally {
      setDeletingBatch(false);
    }
  };
  
  // Get selected batch name for confirmation dialog
  const selectedBatchName = batches.find(b => b.batch_id === selectedBatch)?.import_name || null;

  const stats = importedData?.stats;

  // Calculate display values with proper edge case handling
  const getWinRate = () => {
    if (!stats || stats.total_trades === 0) return "N/A";
    return `${stats.win_rate.toFixed(1)}%`;
  };

  const getAvgWinRR = () => {
    if (!stats || stats.wins === 0) return "N/A";
    return stats.avg_win_rr?.toFixed(2) || "N/A";
  };

  const getAvgRR = () => {
    if (!stats || stats.total_trades === 0) return "N/A";
    return stats.avg_rr?.toFixed(2) || "0.00";
  };

  const getProfitFactor = () => {
    if (!stats) return "N/A";
    if (stats.losses === 0 && stats.wins > 0) return "∞";
    if (stats.profit_factor === null || stats.profit_factor === undefined) return "N/A";
    return formatProfitFactor(stats.profit_factor);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#00E676] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="imported-analytics-page">
      <div className="w-full max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-2 text-white/50 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Analytics</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2" data-testid="imported-analytics-title">
                Imported Analytics
              </h2>
              <p className="text-white/60 text-base font-sans">
                Historical data imported for separate analysis
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Share Button */}
              {importedData?.has_imported_data && (
                <Button
                  onClick={() => setShowShareModal(true)}
                  variant="outline"
                  className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10"
                  data-testid="share-imported-btn"
                >
                  <ShareNetwork size={20} weight="bold" className="mr-2" />
                  Share
                </Button>
              )}
              
              <Button
                onClick={() => setShowImportModal(true)}
                className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
                data-testid="import-more-btn"
              >
                <UploadSimple size={20} weight="bold" className="mr-2" />
                Import Data
              </Button>
              
              {/* Delete All - secondary subtle styling */}
              {importedData?.has_imported_data && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 rounded-md text-sm border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5"
                  data-testid="delete-all-imported-btn"
                >
                  <Trash size={16} weight="bold" />
                  Delete All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Isolation Notice */}
        <div className="mb-6 bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-[#00B8D4] shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-white/80 font-medium mb-1">Isolated Analytics</p>
              <p className="text-white/50">
                Imported data is analyzed separately and does not affect your GENI trading performance, 
                insights, history, or any other core features.
              </p>
            </div>
          </div>
        </div>
        
        {/* Filter Row - Always visible when has data */}
        {importedData?.has_imported_data && (
          <div className="mb-8 flex flex-wrap items-center gap-4">
            <label className="text-white/60 text-sm">Viewing:</label>
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              disabled={compareMode}
              className="bg-[#0A0A0A] border border-white/10 text-white px-4 py-2 rounded-sm text-sm focus:outline-none focus:border-white/30 cursor-pointer min-w-[200px] disabled:opacity-50"
              data-testid="batch-selector"
            >
              <option value="all">All Imports ({importedData?.total_imported || 0} trades)</option>
              {batches.map((batch) => (
                <option key={batch.batch_id} value={batch.batch_id}>
                  {batch.import_name} ({batch.total_trades} trades)
                </option>
              ))}
            </select>
            {selectedBatch !== 'all' && !compareMode && (
              <>
                <button
                  onClick={() => setSelectedBatch('all')}
                  className="text-[#00B8D4] text-sm hover:underline"
                >
                  Show all
                </button>
                
                {/* Delete Batch - contextual to selected batch */}
                <button
                  type="button"
                  onClick={() => setShowDeleteBatchConfirm(true)}
                  className="px-3 py-2 rounded-md text-sm border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5"
                  data-testid="delete-batch-btn"
                >
                  <Trash size={14} weight="bold" />
                  Delete Batch
                </button>
              </>
            )}
            
            {/* Divider */}
            <div className="hidden sm:block h-6 w-px bg-white/10" />
            
            {/* Compare Batches Toggle - Always visible, disabled if < 2 batches */}
            <button
              type="button"
              onClick={() => setCompareMode(!compareMode)}
              disabled={batches.length < 2}
              className={`text-sm px-4 py-2 rounded-sm border transition-colors flex items-center gap-2 ${
                compareMode 
                  ? 'bg-[#00B8D4]/10 border-[#00B8D4] text-[#00B8D4]' 
                  : batches.length < 2
                    ? 'border-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/10 text-white/60 hover:text-white hover:border-white/30'
              }`}
              data-testid="compare-batches-btn"
              title={batches.length < 2 ? 'Import at least 2 batches to compare' : ''}
            >
              <Scales size={16} weight="bold" />
              {compareMode ? 'Exit Compare' : 'Compare Batches'}
            </button>
          </div>
        )}
        
        {/* Compare Mode UI */}
        {compareMode && batches.length >= 2 && (
          <div className="mb-8 bg-[#0A0A0A] border border-white/10 rounded-sm p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Scales size={18} className="text-[#00B8D4]" />
              Select Batches to Compare
              <span className="text-white/40 text-sm font-normal">(2-4 batches)</span>
            </h3>
            
            {/* Batch Selection Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {batches.map((batch, index) => {
                const isSelected = selectedBatchIds.includes(batch.batch_id);
                const color = isSelected ? COMPARISON_COLORS[selectedBatchIds.indexOf(batch.batch_id)] : null;
                
                return (
                  <button
                    key={batch.batch_id}
                    onClick={() => toggleBatchSelection(batch.batch_id)}
                    className={`p-4 rounded-sm border text-left transition-all ${
                      isSelected 
                        ? 'border-current bg-current/10' 
                        : 'border-white/10 hover:border-white/20 bg-[#121212]'
                    }`}
                    style={isSelected ? { borderColor: color, color: color } : {}}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isSelected ? '' : 'text-white'}`}>
                        {batch.import_name}
                      </span>
                      {isSelected && (
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: color }}
                        >
                          <Check size={12} weight="bold" className="text-black" />
                        </div>
                      )}
                    </div>
                    <p className="text-white/40 text-xs mt-1">{batch.total_trades} trades</p>
                  </button>
                );
              })}
            </div>
            
            {selectedBatchIds.length < 2 && (
              <p className="text-white/40 text-sm text-center py-4">
                Select at least 2 batches to compare
              </p>
            )}
            
            {/* Comparison Results */}
            {selectedBatchIds.length >= 2 && (
              <>
                {comparisonLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-[#00B8D4] border-t-transparent rounded-full mx-auto" />
                    <p className="text-white/40 text-sm mt-3">Loading comparison...</p>
                  </div>
                ) : (
                  <>
                    {/* Comparison Cards */}
                    <h4 className="text-white/60 text-sm font-medium mb-3 mt-6">Batch Comparison</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                      {comparisonData.map((batch, index) => (
                        <div 
                          key={batch.batch_id}
                          className="bg-[#121212] border rounded-sm p-4"
                          style={{ borderColor: COMPARISON_COLORS[index] }}
                        >
                          <div 
                            className="text-sm font-medium mb-3 pb-2 border-b border-white/10"
                            style={{ color: COMPARISON_COLORS[index] }}
                          >
                            {batch.import_name}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-white/50">Trades</span>
                              <span className="text-white font-medium">{batch.total_trades}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/50">Win Rate</span>
                              <span className={batch.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]'}>
                                {batch.win_rate}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/50">Avg RR</span>
                              <span className={batch.avg_rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}>
                                {batch.avg_rr}R
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/50">Profit Factor</span>
                              <span className={getProfitFactorColor(batch.profit_factor)}>
                                {batch.profit_factor === 0 ? 'N/A' : batch.profit_factor}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Comparison Chart */}
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-white/60 text-sm font-medium">Visual Comparison</h4>
                        <select
                          value={comparisonMetric}
                          onChange={(e) => setComparisonMetric(e.target.value)}
                          className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-1.5 rounded-sm focus:outline-none"
                        >
                          <option value="win_rate">Win Rate (%)</option>
                          <option value="avg_rr">Average RR</option>
                          <option value="profit_factor">Profit Factor</option>
                          <option value="total_trades">Total Trades</option>
                        </select>
                      </div>
                      
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={comparisonData}
                            margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis 
                              dataKey="import_name" 
                              tick={{ fill: '#888', fontSize: 11 }}
                              angle={-20}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis 
                              tick={{ fill: '#888', fontSize: 11 }}
                              domain={comparisonMetric === 'win_rate' ? [0, 100] : ['auto', 'auto']}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#1a1a1a', 
                                border: '1px solid #333',
                                borderRadius: '4px'
                              }}
                              labelStyle={{ color: '#fff' }}
                              formatter={(value) => [
                                comparisonMetric === 'win_rate' ? `${value}%` : 
                                comparisonMetric === 'avg_rr' ? `${value}R` : value,
                                comparisonMetric.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                              ]}
                            />
                            <Bar dataKey={comparisonMetric} radius={[4, 4, 0, 0]}>
                              {comparisonData.map((entry, index) => (
                                <Cell key={entry.batch_id} fill={COMPARISON_COLORS[index]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {!importedData?.has_imported_data ? (
          /* Empty State */
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <UploadSimple size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
            <p className="text-white/60 text-lg mb-2">No imported data yet</p>
            <p className="text-white/40 text-sm mb-6">
              Import historical trades from CSV to analyze them separately
            </p>
            <Button
              onClick={() => setShowImportModal(true)}
              className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
            >
              <UploadSimple size={20} weight="bold" className="mr-2" />
              Import Your First Data
            </Button>
          </div>
        ) : (
          <>
            {/* Import Summary */}
            <div className="mb-6 text-sm text-white/50">
              <span className="font-mono text-[#00B8D4]">{importedData.total_imported}</span> imported trades 
              across <span className="font-mono">{importedData.batches?.length || 1}</span> import batch(es)
            </div>

            {/* Imported Global Performance */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={24} weight="duotone" className="text-[#00B8D4]" />
                <h3 className="text-2xl font-bold font-heading">Imported Global Performance</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <MetricCard
                  title="Total Trades"
                  value={stats?.total_trades || 0}
                  subtitle={`${stats?.wins || 0}W / ${stats?.losses || 0}L`}
                  icon={ChartBar}
                />
                <MetricCard
                  title="Win Rate"
                  value={getWinRate()}
                  subtitle={stats?.win_rate >= 50 ? "Above average" : "Below average"}
                  icon={Target}
                  highlight={stats?.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
                />
                <MetricCard
                  title="Avg Win RR"
                  value={getAvgWinRR()}
                  subtitle="Winning trades only"
                  icon={TrendUp}
                  highlight="text-[#00E676]"
                />
                <MetricCard
                  title="Average RR"
                  value={getAvgRR()}
                  subtitle="All trades"
                  icon={TrendUp}
                  highlight={parseFloat(stats?.avg_rr) >= 2 ? "text-[#00E676]" : "text-white"}
                />
                <MetricCard
                  title="Profit Factor"
                  value={getProfitFactor()}
                  subtitle={getProfitFactorLabel(stats?.profit_factor)}
                  icon={Trophy}
                  highlight={getProfitFactorColor(stats?.profit_factor)}
                />
              </div>
            </div>

            {/* Imported Setup Performance */}
            {stats?.setup_breakdown && stats.setup_breakdown.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Target size={24} weight="duotone" className="text-[#00B8D4]" />
                  <h3 className="text-2xl font-bold font-heading">Imported Setup Performance</h3>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#0A0A0A]/50 border-b border-white/10">
                          <th className="px-4 py-3 text-left text-white/40 text-xs font-mono uppercase">Setup</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Trades</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Win Rate</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Avg RR</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Profit Factor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.setup_breakdown.map((setup, idx) => {
                          const pf = setup.profit_factor;
                          const pfDisplay = pf === 0 && setup.total_trades > 0 
                            ? (setup.win_rate === 100 ? "∞" : "N/A")
                            : formatProfitFactor(pf);
                          
                          return (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                              <td className="px-4 py-3 text-white font-medium">{setup.setup}</td>
                              <td className="px-4 py-3 text-right text-white/70 font-mono">{setup.total_trades}</td>
                              <td className={`px-4 py-3 text-right font-mono ${
                                setup.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'
                              }`}>
                                {setup.win_rate?.toFixed(1)}%
                              </td>
                              <td className={`px-4 py-3 text-right font-mono ${
                                setup.avg_rr >= 2 ? 'text-[#00E676]' : 'text-white/70'
                              }`}>
                                {setup.avg_rr?.toFixed(2)}
                              </td>
                              <td className={`px-4 py-3 text-right font-mono ${getProfitFactorColor(pf)}`}>
                                {pfDisplay}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Imported Timeframe Performance */}
            {stats?.timeframe_breakdown && stats.timeframe_breakdown.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Sun size={24} weight="duotone" className="text-[#00B8D4]" />
                  <h3 className="text-2xl font-bold font-heading">Imported Timeframe Performance</h3>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#0A0A0A]/50 border-b border-white/10">
                          <th className="px-4 py-3 text-left text-white/40 text-xs font-mono uppercase">Timeframe</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Trades</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Win Rate</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Avg RR</th>
                          <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Profit Factor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.timeframe_breakdown.map((tf, idx) => {
                          const pf = tf.profit_factor;
                          const pfDisplay = pf === 0 && tf.total_trades > 0 
                            ? (tf.win_rate === 100 ? "∞" : "N/A")
                            : formatProfitFactor(pf);
                          
                          return (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                              <td className="px-4 py-3 text-white font-medium font-mono">{tf.timeframe}</td>
                              <td className="px-4 py-3 text-right text-white/70 font-mono">{tf.total_trades}</td>
                              <td className={`px-4 py-3 text-right font-mono ${
                                tf.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'
                              }`}>
                                {tf.win_rate?.toFixed(1)}%
                              </td>
                              <td className={`px-4 py-3 text-right font-mono ${
                                tf.avg_rr >= 2 ? 'text-[#00E676]' : 'text-white/70'
                              }`}>
                                {tf.avg_rr?.toFixed(2)}
                              </td>
                              <td className={`px-4 py-3 text-right font-mono ${getProfitFactorColor(pf)}`}>
                                {pfDisplay}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Import Modal */}
      <ImportAnalyticsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={() => {
          fetchBatches();
          fetchImportedData(selectedBatch);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete All Imported Data?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will permanently delete all {importedData?.total_imported || 0} imported trades. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/10 hover:bg-white/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              disabled={deleting}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90"
            >
              {deleting ? "Deleting..." : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Batch Confirmation Dialog */}
      <AlertDialog open={showDeleteBatchConfirm} onOpenChange={setShowDeleteBatchConfirm}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete import batch?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Are you sure you want to delete this imported batch and all trades related to it? This action cannot be undone.
              {selectedBatchName && (
                <span className="block mt-2 text-white/80 font-mono text-sm">
                  Batch: "{selectedBatchName}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/10 hover:bg-white/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBatch}
              disabled={deletingBatch}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90"
            >
              {deletingBatch ? "Deleting..." : "Delete Batch"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Paywall Modal - Value Proposition (for non-yearly users) */}
      <ImportPaywallModal
        isOpen={showPaywallModal}
        onClose={() => {
          setShowPaywallModal(false);
          navigate('/analytics');
        }}
        onUpgradeClick={handlePaywallUpgradeClick}
      />

      {/* Payment Modal - After clicking upgrade in paywall */}
      <CryptoPaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setIsUpgradeFlow(false);
          navigate('/analytics');
        }}
        onPaymentSubmitted={() => {
          setShowPaymentModal(false);
          setIsUpgradeFlow(false);
          // Recheck access after payment submitted
          checkAccessAndFetch();
        }}
        initialPlan="yearly"
        isUpgrade={isUpgradeFlow}
        upgradePrice={79}
        onBack={() => {
          setShowPaymentModal(false);
          setShowPaywallModal(true);
        }}
      />
      
      {/* Share Modal for Imported Analytics */}
      <ImportedShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        selectedBatch={selectedBatch}
        selectedBatchName={selectedBatchName}
        compareMode={compareMode}
        selectedBatchIds={selectedBatchIds}
        batches={batches}
      />
    </div>
  );
}
